import React, { CSSProperties, ReactNode, useContext } from "react";
import { ErrorText, FormFieldTitle } from "features/bots/common/styles.ts";
import useMultiSymbolMarginSelector from "@shared/features/bots/BotForm/MultiSymbolMarginSelector/useMultiSymbolMarginSelector.ts";
import Modal from "common/Modal/Modal.tsx";
import Flex from "common/Boxes/Flex.tsx";
import Text from "common/Typography/Text.tsx";
import styled, { useTheme } from "styled-components";
import Button from "common/Buttons/Button.tsx";
import InputBox from "common/Inputs/InputBox.tsx";
import TitleWithToggle from "features/bots/BotForm/common/TitleWithToggle.tsx";
import {
  OverrideType,
  ToggleField,
} from "@shared/features/bots/BotForm/common/types.ts";
import {
  BaseBotData,
  BotData,
  LanguageNameSpaces,
  OverrideFieldType,
} from "@shared/types";
import { HelperTooltip } from "common/Tooltips/HelperTooltip.tsx";
import {
  BOT_MARGIN_DATA_MODIFIED_MESSAGE,
  SHOW_MODIFIED_SYMBOLS_TITLE,
} from "@shared/features/bots/BotForm/common/constants.ts";
import OverrideToggle from "features/bots/BotForm/common/OverrideToggle.tsx";
import MultiSymbolMarginProvider from "@shared/features/bots/BotForm/MultiSymbolMarginSelector/MultiSymbolMarginProvider.tsx";
import SymbolsList from "features/bots/BotForm/MultiSymbolMargin/SymbolsList.tsx";
import SelectAll from "features/bots/BotForm/MultiSymbolMargin/SelectAll.tsx";
import { BotFormData } from "@shared/features/bots/BotForm/common/BotForm.context.ts";
import Switch from "common/Buttons/Switch.tsx";
import GlobalSettings from "features/bots/BotForm/MultiSymbolMargin/GlobalSettings.tsx";
import Drawer from "common/Drawers/Drawer.tsx";
import { useBottomNavigator } from "utils/hooks/misc.tsx";
import PairButtonsWithSearch from "features/bots/BotForm/MultiSymbolMargin/PairButtonsWithSearch.tsx";
import FlatList from "common/Lists/FlatList.tsx";
import SymbolRow from "features/bots/BotForm/MultiSymbolMargin/SymbolRow.tsx";
import { MultiSymbolMarginContext } from "@shared/features/bots/BotForm/MultiSymbolMarginSelector/MultiSymbolMargin.context.ts";
import EmptySymbols from "features/bots/BotForm/MultiSymbolMargin/EmptySymbols.tsx";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";

interface MultiSymbolMarginSelectProps {
  style?: CSSProperties;
  getTrigger?: (previewText: string) => ReactNode;
  isViewOnly?: boolean;
  botData?: BaseBotData | BotData;
  botFormData?: BotFormData;
  isSymbolsOnly?: boolean;
  alwaysDisplay?: boolean;
}

const MultiSymbolMarginSelect = ({
  style,
  getTrigger,
  isViewOnly,
  botData,
  botFormData,
  isSymbolsOnly,
  alwaysDisplay,
}: MultiSymbolMarginSelectProps) => {
  return (
    <MultiSymbolMarginProvider
      isViewOnly={isViewOnly}
      botData={botData}
      botFormData={botFormData}
      isSymbolsOnly={isSymbolsOnly}
      alwaysDisplay={alwaysDisplay}
    >
      <InnerMultiSymbolMarginSelect style={style} getTrigger={getTrigger} />
    </MultiSymbolMarginProvider>
  );
};

export default MultiSymbolMarginSelect;

const InnerMultiSymbolMarginSelect = ({
  style,
  getTrigger: propsGetTrigger,
}: MultiSymbolMarginSelectProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.BOTS);
  const theme = useTheme();
  const { symbolOptions, isSymbolsOnly } = useContext(
    MultiSymbolMarginContext
  )!;
  const {
    isOpen,
    setIsOpen,
    pairOptions,
    footerData,
    selectorPreview,
    title,
    confirmModalData,
    shouldDisplay,
    helperData,
    hasLeverageValueModifiedError,
    originalMarginModifiedHelperData,
    isViewOnly,
    showOnlyModifiedSymbols,
    setShowOnlyModifiedSymbols,
    defaultChannelValue,
    resetLeverageOverride,
  } = useMultiSymbolMarginSelector({});
  const { baseShouldDisplay: isBottomNavigator } = useBottomNavigator();

  if (!shouldDisplay) return null;

  const footer = (
    <Flex style={isBottomNavigator ? { padding: "8px 16px" } : undefined}>
      {isViewOnly ? (
        <div />
      ) : (
        <Button onClick={footerData.onCancel}>Cancel</Button>
      )}
      <Flex>
        {!isViewOnly && (
          <Button
            onClick={footerData.onApply}
            disabled={footerData.applyButtonDisabled}
            style={{ marginRight: 8 }}
          >
            Apply
          </Button>
        )}
        <Button type="primary" onClick={footerData.onOk}>
          {isViewOnly ? "Close" : "OK"}
        </Button>
      </Flex>
    </Flex>
  );

  const openModal = () => setIsOpen(true);

  const getTrigger = () => {
    if (propsGetTrigger) {
      return (
        <div onClick={openModal} style={{ cursor: "pointer" }}>
          {propsGetTrigger(selectorPreview)}
        </div>
      );
    }
    return <InputBox onClick={openModal}>{selectorPreview}</InputBox>;
  };

  const globalSettings = !isSymbolsOnly && (
    <>
      <GlobalSettings />
      <Flex style={{ marginTop: 16 }}>
        <Text type="secondary">Symbols</Text>
        <Flex
          onClick={() => setShowOnlyModifiedSymbols(!showOnlyModifiedSymbols)}
        >
          <Switch
            checked={showOnlyModifiedSymbols}
            size="small"
            style={{ marginRight: 12 }}
          />
          <Text>{t(SHOW_MODIFIED_SYMBOLS_TITLE)}</Text>
        </Flex>
      </Flex>
    </>
  );
  const selectAll = <SelectAll />;
  const pairButtons = (
    <PairButtonsWithSearch
      pairOptions={pairOptions}
      style={{ margin: "12px 0 4px 0" }}
      bordered={false}
      hideTitle={true}
    />
  );

  const modalContent = (
    <Modal
      open={isOpen}
      onCancel={footerData.onCancel}
      title={title}
      styles={{
        body: {
          padding: 0,
        },
      }}
      footer={footer}
      width={560}
    >
      <div style={{ padding: "0 16px" }}>
        {!isSymbolsOnly && globalSettings}
        {selectAll}
        {pairButtons}
      </div>
      <div
        style={{
          flex: 1,
          overflow: "hidden",
          height: 300,
        }}
      >
        <SymbolsList />
      </div>
    </Modal>
  );

  const drawerContent = (
    <StyledDrawer
      open={isOpen}
      onClose={footerData.onCancel}
      styles={{
        body: {
          padding: "20px 0 0 0",
          display: "flex",
          flexDirection: "column",
        },
      }}
      destroyOnHidden={false}
    >
      <FlatList
        data={symbolOptions || []}
        renderTopContainer={() => {
          return (
            <div>
              {globalSettings}
              {selectAll}
              {pairButtons}
            </div>
          );
        }}
        renderContent={({ item }) => <SymbolRow data={item} />}
        emptyState={<EmptySymbols />}
        showScrollbar={true}
      />
      {footer}
    </StyledDrawer>
  );

  const content = (
    <>
      {getTrigger()}
      {hasLeverageValueModifiedError && (
        <ErrorText style={{ color: theme.colors.darkYellow }} as="div">
          {BOT_MARGIN_DATA_MODIFIED_MESSAGE}
          <HelperTooltip {...originalMarginModifiedHelperData} />
        </ErrorText>
      )}
      {isBottomNavigator ? drawerContent : modalContent}
    </>
  );

  return (
    <div style={style}>
      {isViewOnly ? (
        content
      ) : (
        <TitleWithToggle
          title={
            <FormFieldTitle style={{ margin: 0 }}>
              Leverage
              <HelperTooltip {...helperData} />
            </FormFieldTitle>
          }
          content={
            <>
              <div style={{ maxWidth: "50%" }}>{content}</div>
              <OverrideToggle overrideField={OverrideFieldType.LEVERAGE} />
            </>
          }
          fieldType={ToggleField.MARGIN}
          onPersonalClick={openModal}
          defaultChannelValue={defaultChannelValue}
          onOverrideChange={(overrideType) => {
            if (overrideType === OverrideType.CHANNEL) {
              resetLeverageOverride();
            }
          }}
        />
      )}
      <Modal
        title={confirmModalData.title}
        open={confirmModalData.isOpen}
        onCancel={confirmModalData.onCancel}
        onOk={confirmModalData.onOk}
        okText={confirmModalData.okText}
        cancelText={confirmModalData.cancelText}
      >
        {confirmModalData.texts.map((text, index) => (
          <div style={{ marginTop: index ? 6 : 0 }} key={index}>
            <Text>{text}</Text>
          </div>
        ))}
      </Modal>
    </div>
  );
};

const StyledDrawer = styled(Drawer)`
  .ant-drawer-body {
    padding-bottom: 0 !important;
  }
`;
